import React from 'react';
import { money } from '../../utils/helpers';
import { LINE_SOURCES, CHARGE_RULES, sourceOf, lineCountText, linesSummary } from '../../utils/costLines';

// Phone version of the lines of one booking: one card per line, fields stacked.
// Same props and logic as the desktop grid.
export default function CostLinesEditorMobile({ lines, setLine, addLine, removeLine, lineInfo, functions, categoryList }) {
  // What is owed, and what it comes to with every "if needed" line taken and
  // every range at its ceiling (a quotation always has both numbers).
  const sum = linesSummary(lines, null);
  const total = lines.reduce((s, l) => s + (lineInfo(l).optional ? 0 : lineInfo(l).amount), 0);
  return (
    <div className="mn-lines m">
      {lines.map((l) => {
        const m = lineInfo(l);
        const fixed = l.Basis === 'Fixed';
        const src = sourceOf(l.LiveSource);
        const countText = lineCountText(l, m);
        return (
          <div key={l.key} className="mn-line-card">
            <div className="mn-line-card-top">
              <input value={l.Title} placeholder="Item, e.g. Deluxe room" onChange={e => setLine(l.key, 'Title', e.target.value)} />
              <strong className={m.optional ? 'muted' : undefined}>{money(m.amount)}</strong>
              <button type="button" className="icon-btn danger" onClick={() => removeLine(l.key)}>✕</button>
            </div>
            <div className="mn-seg m">
              <button type="button" className={!fixed ? 'on' : ''} onClick={() => setLine(l.key, 'Basis', 'Qty')}>Qty × rate</button>
              <button type="button" className={fixed ? 'on' : ''} onClick={() => setLine(l.key, 'Basis', 'Fixed')}>Fixed amount</button>
            </div>
            <label className="field">Function
              <select value={l.FunctionId} onChange={e => setLine(l.key, 'FunctionId', e.target.value)}>
                <option value="">Whole event</option>
                {(functions || []).map(f => <option key={f.FunctionId} value={String(f.FunctionId)}>{f.FunctionName}</option>)}
              </select>
            </label>
            {fixed ? (
              <label className="field mn-amt">Amount (₹)
                <input inputMode="decimal" value={l.Amount} onChange={e => setLine(l.key, 'Amount', e.target.value)} />
              </label>
            ) : (
              <>
                <div className="mn-pair mn-amt">
                  <label className="field">Qty / minimum
                    <input inputMode="decimal" value={l.Quantity} placeholder={m.linked ? String(m.live) : ''} onChange={e => setLine(l.key, 'Quantity', e.target.value)} />
                  </label>
                  <label className="field">Rate (₹)
                    <input inputMode="decimal" value={l.Rate} onChange={e => setLine(l.key, 'Rate', e.target.value)} />
                  </label>
                </div>
                <div className="mn-pair">
                  <label className="field">From
                    <input type="date" value={l.FromDate} onChange={e => setLine(l.key, 'FromDate', e.target.value)} />
                  </label>
                  <label className="field">To
                    <input type="date" value={l.ToDate} onChange={e => setLine(l.key, 'ToDate', e.target.value)} />
                  </label>
                </div>
                {l.FromDate && l.ToDate ? (
                  <div className="muted" style={{ fontSize: 12.5 }}>{m.days} night{m.days === 1 ? '' : 's'}</div>
                ) : (
                  <label className="field">Days
                    <input inputMode="numeric" value={l.Days} onChange={e => setLine(l.key, 'Days', e.target.value)} />
                  </label>
                )}
                <label className="field">Quantity follows
                  <select value={l.LiveSource} onChange={e => setLine(l.key, 'LiveSource', e.target.value)}>
                    {LINE_SOURCES.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
                  </select>
                </label>
                {src.value && (
                  <div className="mn-seg m">
                    {CHARGE_RULES.map(r => (
                      <button key={r.value} type="button" className={l.ChargeRule === r.value ? 'on' : ''} onClick={() => setLine(l.key, 'ChargeRule', r.value)}>{r.label}</button>
                    ))}
                  </div>
                )}
                {src.needsFunction && !l.FunctionId && <div className="mn-due" style={{ fontSize: 12.5 }}>Pick the function whose {src.unit} this follows</div>}
                {countText && <div className="muted" style={{ fontSize: 12.5 }}>{countText}</div>}
              </>
            )}
            <label className="field">Category (blank = the expense's)
              <input list={categoryList} value={l.Category} onChange={e => setLine(l.key, 'Category', e.target.value)} />
            </label>
            {/* A quotation's two extras: a price given as a range ("varmala
                4,000 to 6,000") and a line taken only if needed. */}
            <label className="field">Up to (₹) — blank if the price is firm
              <input inputMode="decimal" value={l.RateMax} placeholder="—"
                onChange={e => setLine(l.key, 'RateMax', e.target.value)} />
            </label>
            <label className="mn-tick">
              <input type="checkbox" checked={Number(l.Optional || 0) === 1}
                onChange={e => setLine(l.key, 'Optional', e.target.checked ? 1 : 0)} />
              Taken only if needed — not counted in what is owed
            </label>
            {(m.optional || m.ranged) && (
              <div className="muted" style={{ fontSize: 12.5 }}>
                {m.optional ? 'If needed' : 'Up to'} {money(m.maxAmount)}
              </div>
            )}
          </div>
        );
      })}
      <div className="mn-lines-foot">
        <button type="button" className="btn light sm" onClick={() => addLine()}>+ Line</button>
        <button type="button" className="btn light sm" onClick={() => addLine({ Basis: 'Fixed', FromDate: '', ToDate: '' })}>+ Fixed</button>
        <button type="button" className="btn light sm" onClick={() => addLine({ Basis: 'Fixed', FromDate: '', ToDate: '', Optional: 1 })}>+ If needed</button>
        <strong>{money(total)}</strong>
        {sum.upTo > total && <span className="muted" style={{ fontSize: 12 }}>up to {money(sum.upTo)}</span>}
      </div>
    </div>
  );
}
